import { EmptyState } from "@/components/EmptyState";
import { LoadingPanel } from "@/components/LoadingPanel";
import { StatCard } from "@/components/StatCard";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useIsContractor } from "@/hooks/useIsContractor";
import { useTenancy } from "@/hooks/useTenancy";
import { formatDateGB, formatPence, formatRentDueDay, nextRentDueDateUTC } from "@/lib/format";
import { ArrowRight, FileText, Home, User, Wrench } from "lucide-react";
import { Link, Navigate } from "react-router-dom";

export function Dashboard() {
  const { tenancies, loading, error } = useTenancy();
  const { isAdmin, loading: adminLoading } = useIsAdmin();
  const { isContractor, loading: contractorLoading } = useIsContractor();

  if (loading || adminLoading || contractorLoading) return <LoadingPanel />;
  // Admins always land in the admin console — they can navigate to / manually
  // if they want the tenant view of their own portfolio.
  if (isAdmin) return <Navigate to="/admin" replace />;
  // Contractors with no tenancy on file land here straight from /login. Punt
  // them to their own portal.
  if (isContractor && tenancies.length === 0) return <Navigate to="/contractor" replace />;
  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Something went wrong</CardTitle>
          <CardDescription>{error}</CardDescription>
        </CardHeader>
      </Card>
    );
  }
  if (tenancies.length === 0) {
    return (
      <EmptyState
        title="No active tenancy on file"
        description="Once your tenancy is set up, it will appear here. If you think this is a mistake, get in touch."
        action={
          <Button variant="secondary" size="sm" asChild>
            <a href="mailto:hello@triniumproperty.co.uk?subject=Tenant%20portal%20access">
              hello@triniumproperty.co.uk
            </a>
          </Button>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-2">
        <span className="text-xs uppercase tracking-[0.18em] text-ink-dim">Welcome back</span>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Your tenancy</h1>
      </header>
      {tenancies.map(({ tenancy, property }) => {
        const next = nextRentDueDateUTC(tenancy.rent_due_day);
        return (
          <Card key={tenancy.id}>
            <CardHeader>
              <CardTitle>{property.display_name}</CardTitle>
              <CardDescription>
                {[property.line1, property.line2, property.postcode].filter(Boolean).join(", ")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-3 gap-4">
                <StatCard label="Monthly rent" value={formatPence(tenancy.monthly_rent_pence)} />
                <StatCard
                  label="Next rent due"
                  value={formatRentDueDay(tenancy.rent_due_day)}
                  hint={formatDateGB(next)}
                />
                <StatCard
                  label="Status"
                  value={tenancy.status[0]?.toUpperCase() + tenancy.status.slice(1)}
                />
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
                <DashboardLink to="/property" icon={<Home size={16} />}>
                  Property &amp; compliance
                </DashboardLink>
                <DashboardLink to="/rent" icon={<FileText size={16} />}>
                  Rent &amp; bank details
                </DashboardLink>
                <DashboardLink to="/repairs" icon={<Wrench size={16} />}>
                  Repairs
                </DashboardLink>
                <DashboardLink to="/profile" icon={<User size={16} />}>
                  Your details
                </DashboardLink>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function DashboardLink({
  to,
  icon,
  children,
}: {
  to: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      className="group rounded-xl border border-line bg-bg-card/30 p-4 flex items-center justify-between text-sm text-ink hover:border-white/20 transition"
    >
      <span className="flex items-center gap-2">
        {icon}
        {children}
      </span>
      <ArrowRight size={16} className="text-ink-dim group-hover:text-accent transition" />
    </Link>
  );
}
